import { apiClient } from './api';
import type {
  DeferredBookingInquiry,
  DeferredBookingDecisionRequest,
  DeferredBookingDecisionOutcome,
} from '../types/deferredBookingInquiry';
import type {
  OwnerDeferredBookingInquiryPage,
  DeferredInquiryDecisionStatus,
} from '../types/supportCase';

const normalizeInquiry = (raw: Record<string, unknown>): DeferredBookingInquiry => ({
  bookingId: (raw.bookingId as string) ?? '',
  apartmentId: (raw.apartmentId as string) ?? null,
  status: (raw.status as string) ?? null,
  checkInDate: (raw.checkInDate as string) ?? null,
  checkOutDate: (raw.checkOutDate as string) ?? null,
  nights: (raw.nights as number) ?? null,
  totalPrice: (raw.totalPrice as string) ?? null,
  pricePerNight: (raw.pricePerNight as string) ?? null,
  ownerReviewExpiresAt: (raw.ownerReviewExpiresAt as string) ?? null,
  guestActionExpiresAt: (raw.guestActionExpiresAt as string) ?? null,
  guestInfo: (raw.guestInfo as DeferredBookingInquiry['guestInfo']) ?? null,
  tripDetails: (raw.tripDetails as DeferredBookingInquiry['tripDetails']) ?? null,
});

const normalizeDecisionOutcome = (raw: Record<string, unknown>): DeferredBookingDecisionOutcome => ({
  bookingId: (raw.bookingId as string) ?? null,
  status: (raw.status as string) ?? null,
  guestActionExpiresAt: (raw.guestActionExpiresAt as string) ?? null,
  secureBookingLinkSent: (raw.secureBookingLinkSent as boolean) ?? null,
  datesReleased: (raw.datesReleased as boolean) ?? null,
});

export const deferredBookingInquiryService = {
  async listPendingInquiries(): Promise<readonly DeferredBookingInquiry[]> {
    const response = await apiClient.get<Record<string, unknown>[]>(
      '/owner/deferred-bookings/pending',
    );
    return response.data.map(normalizeInquiry);
  },

  async listInquiries(params?: {
    decisionStatus?: ReadonlyArray<DeferredInquiryDecisionStatus>;
    page?: number;
    size?: number;
    sort?: string;
  }): Promise<OwnerDeferredBookingInquiryPage> {
    const queryParams: Record<string, unknown> = {};
    if (params?.decisionStatus) queryParams['decisionStatus'] = [...params.decisionStatus];
    if (params?.page !== undefined) queryParams['page'] = params.page;
    if (params?.size !== undefined) queryParams['size'] = params.size;
    if (params?.sort) queryParams['sort'] = params.sort;

    const response = await apiClient.get<OwnerDeferredBookingInquiryPage>(
      '/owner/deferred-bookings/inquiries',
      { params: queryParams },
    );
    return response.data;
  },

  async decideInquiry(
    bookingId: string,
    request: DeferredBookingDecisionRequest,
  ): Promise<DeferredBookingDecisionOutcome> {
    const response = await apiClient.post<Record<string, unknown>>(
      `/owner/deferred-bookings/${bookingId}/decision`,
      request,
    );
    return normalizeDecisionOutcome(response.data);
  },
};
